/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import loadable from '@loadable/component';

import validateEmail from 'commons/validateEmail';
import capitalizeFirstLetter from 'commons/capitalizeFirstLetter';
import BriteverifyClient from 'commons/britverify-client';
import isLocalhost from 'commons/isLocalhost';
import {
  INVALID_NAME_CHARACTERS_REGEX,
  RECAPTCHA_SITE_KEY,
} from 'commons/constants';
import WarrantyForm from 'components/consumer/WarrantyForm';
import CtaButton from 'components/essentials/Cta/CtaButton';
import FrameContainer from 'components/consumer/FrameContainer';
import LabeledTextarea from 'components/consumer/LabeledTextarea';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import FlatButtonFilters from 'components/consumer/FlatButtonFilters';
import Radio from 'components/essentials/Radio';
import IconAskAQuestion from 'components/icons/IconAskAQuestion';
import { colorBrandLightest } from 'styles/layout/colors';
import FrameContainerHeader from 'components/consumer/FrameContainerHeader';
import useUserData from 'data-hooks/useUserData';
import questionsAndAnswers from './questionsAndAnswers.json';
import CtaLink from '../../essentials/Cta/CtaLink';

const AnimateHeight = loadable(() => import('react-animate-height'));

const ContactEnquiryFormUI = ({ error, loading, onSubmit }) => {
  const { email: userEmail, firstName } = useUserData();

  // Form field state
  const [name, setName] = useState(
    capitalizeFirstLetter((firstName || '').toLowerCase())
  );
  const [email, setEmail] = useState((userEmail || '').toLowerCase());
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [message, setMessage] = useState('');
  const [reCaptchaValue, setReCaptchaValue] = useState('');
  const receiveEmailUpdates = false;

  // Validation state
  const [isNameValid, setIsNameValid] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isSelectedTopicValid, setIsSelectedTopicValid] = useState(true);
  const [isMessageValid, setIsMessageValid] = useState(true);
  const [isReCaptchaValid, setIsReCaptchaValid] = useState(true);

  const _isNameValid = !!name;
  const _isEmailValid = !!email && validateEmail(email);
  const _isSelectedTopicValid = !!selectedTopic;
  const _isMessageValid = !!message;
  const _isSelectedQuestionValid = !!selectedQuestion;

  // Disable reCaptcha check on localhost
  const _isReCaptchaValid = !isLocalhost() ? !!reCaptchaValue : true;
  const isOptionSelected = _isSelectedTopicValid && _isSelectedQuestionValid;

  const isSubmitValid =
    _isNameValid &&
    _isEmailValid &&
    _isSelectedTopicValid &&
    _isSelectedQuestionValid &&
    _isMessageValid &&
    _isReCaptchaValid;

  const validateAndSubmit = () => {
    if (isSubmitValid) {
      onSubmit({
        name,
        email: email.trim(),
        subject: selectedTopic,
        question: selectedQuestion || 'Other',
        tag: selectedTag,
        message,
        subscribe: receiveEmailUpdates,
        phone: '',
        reCaptchaValue,
      });
    }
    setIsNameValid(_isNameValid);
    setIsEmailValid(_isEmailValid);
    setIsSelectedTopicValid(_isSelectedTopicValid);
    setIsMessageValid(_isMessageValid);
    setIsReCaptchaValid(_isReCaptchaValid);
  };

  return (
    <FrameContainer wide className="max-[767px]:!pb-8 md:!rounded-none">
      <FrameContainerHeader
        icon={<IconAskAQuestion color={colorBrandLightest} />}
        title="Ask us a question"
        subtitle="Contact one of our experts and we’ll get back to you as soon as possible."
      />

      <LabeledTextInput
        label="Your name"
        value={name}
        onChange={e => {
          setIsNameValid(true);
          setName(e.target.value);
        }}
        onBlur={evt => {
          if (INVALID_NAME_CHARACTERS_REGEX.test(evt.target.value)) {
            setIsNameValid(false);
          } else {
            setIsNameValid(true);
          }
        }}
        errorMessage={
          !isNameValid ? 'Please enter valid characters only.' : undefined
        }
      />

      <LabeledTextInput
        label="Email address"
        value={email}
        onChange={e => {
          setIsEmailValid(true);
          setEmail(e.target.value);
        }}
        onBlur={async evt => {
          const updatedEmail = evt.target.value.trim();

          if (email !== updatedEmail) {
            setEmail(updatedEmail);
          }

          const validate = await BriteverifyClient.emailLookup(updatedEmail);
          if (!validate?.valid && validate?.message) {
            setIsEmailValid(false);
          } else {
            setIsEmailValid(true);
          }
        }}
        errorMessage={
          !isEmailValid ? 'Please enter a valid email address.' : undefined
        }
      />

      <div className="mt-8 mx-0 mb-10">
        <div className="text-lg font-bold mb-3">
          What can we help you with today?
        </div>

        <FlatButtonFilters
          items={questionsAndAnswers.map(topic => ({
            label: topic.topicLabel,
            id: topic.topicId,
          }))}
          selectedItem={selectedTopic}
          setSelectedItem={topicId => {
            setIsSelectedTopicValid(true);

            if (topicId !== selectedTopic) {
              setSelectedTag('');
              setSelectedQuestion('');
              setSelectedTopic(topicId);
            }
          }}
        />

        {!isSelectedTopicValid && (
          <div className="text-red text-xs font-bold mt-[3px]">
            Please select an option.
          </div>
        )}
      </div>

      {!!selectedTopic &&
        !!questionsAndAnswers.find(
          topic => topic.topicId === selectedTopic
        ) && (
          <div className="mt-8 mx-0 mb-10 [border-top:1px_solid_#f1f1f1] pt-8">
            <div className="text-lg font-bold mb-3">
              We can help! What&apos;s your question?
            </div>

            <div>
              {questionsAndAnswers
                .find(topic => topic.topicId === selectedTopic)
                .questions.map(
                  (
                    {
                      question,
                      answer,
                      tag,
                      warrantyFooter,
                      warrantyForm = false,
                    },
                    questionIndex
                  ) => (
                    <>
                      <Radio
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${selectedTopic}-${questionIndex}`}
                        onChange={() => {
                          setSelectedTag(tag);
                          setSelectedQuestion(question);
                        }}
                        checked={selectedQuestion === question}
                      >
                        <span
                          className={classNames('text-base', {
                            'text-base font-bold':
                              selectedQuestion === question,
                          })}
                        >
                          {question}
                        </span>
                      </Radio>

                      {(answer.length > 0 ||
                        warrantyForm ||
                        warrantyFooter) && (
                        <AnimateHeight
                          duration={500}
                          height={
                            selectedQuestion === question &&
                            question !== 'Other'
                              ? 'auto'
                              : 0
                          }
                        >
                          <div className="bg-gray-light8 rounded mt-2 mx-0 mb-4 p-8 [&_p]:text-base [&_p_a]:ml-1 [&_p+p]:mt-5">
                            {answer.map(answerParagraph => (
                              <p
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                  __html: answerParagraph,
                                }}
                              />
                            ))}
                            {warrantyForm && (
                              <WarrantyForm
                                email={userEmail || null}
                                redirectToStep2
                                variant="ContactEnquiryForm"
                              />
                            )}
                            {warrantyFooter && (
                              <p className="ContactEnquiryFormUI__question-warrantyFooter">
                                If you’d like to submit a warranty request,
                                please see our
                                <CtaLink
                                  to="/issues/"
                                  color="brand"
                                  target="_blank"
                                  hasArrow={false}
                                >
                                  warranty form.
                                </CtaLink>
                              </p>
                            )}
                          </div>
                        </AnimateHeight>
                      )}
                    </>
                  )
                )}
            </div>
          </div>
        )}

      <LabeledTextarea
        label="Your Message. Please include order number where applicable."
        value={message}
        onChange={e => {
          setIsMessageValid(true);
          setMessage(e.target.value);
        }}
        errorMessage={
          !isMessageValid
            ? 'Please tell us more about what we can help you with.'
            : undefined
        }
      />

      <div className="mb-8">
        <ReCAPTCHA
          name="reCaptchaValue"
          sitekey={RECAPTCHA_SITE_KEY}
          onChange={value => {
            setReCaptchaValue(value);
            if (value) setIsReCaptchaValid(true);
          }}
        />
        {!isReCaptchaValid && (
          <div className="text-red text-xs font-bold mt-[3px]">
            Please confirm that you are not a robot
          </div>
        )}
      </div>

      <div className="ContactEnquiryFormUI__submit-section">
        <CtaButton
          block
          spacing="xs"
          disabled={!isSubmitValid || loading}
          onClick={validateAndSubmit}
          isLoading={loading}
        >
          Send your question
        </CtaButton>
        {error && (
          <div className="text-red text-xs font-bold mt-[3px]">{error}</div>
        )}
        {!isOptionSelected && (
          <div className="text-xs font-bold mt-[3px] text-gray text-center">
            Please select an option above
          </div>
        )}
      </div>
    </FrameContainer>
  );
};

ContactEnquiryFormUI.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};

export default ContactEnquiryFormUI;
